import getAllItemViewsByType from 'checkout/selectors/view/getAllItemViewsByType'
import {
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_EXPERIENCE,
  OFFER_TYPE_EXPERIENCE_TRANSFER,
  PRODUCT_TYPE_CAR_HIRE,
  PRODUCT_TYPE_TOUR_ULTRA_LUX,
  PRODUCT_TYPE_TOUR_PREMIUM,
  PRODUCT_TYPE_TOUR_DELUXE,
  PRODUCT_TYPE_TOUR_SIGNATURE_SERIES
} from 'constants/offer'
import { sum, unique } from 'lib/array/arrayUtils'

import { match } from 'ts-pattern'
import { isTourV2Offer, isCarHireOffer, isLpcHotelOffer, isBedbankOffer, isExperienceOffer, isCruiseOffer, isLEHotel } from 'lib/offer/offerTypes'
import { AgentHubGetOffersCommissionBody } from '../agentHubCommission'
import { DIRECT_TOUR } from 'constants/tours'
import { actions } from '@luxuryescapes/contract-svc-agent'
import { mapBedbankPackageToAgentHubPayload, mapFlashPackageToAgentHubPayload, mapLPCPackageToAgentHubPayload } from './agentHubMap'

type ServerAgentCommission = actions['calculateCartCommission']['responses']['200']['content']['application/json']['result']
type ServerOfferCommission = actions['calculateOffersCommission']['responses']['200']['content']['application/json']['result'][number]

type CalculateCartCommissionBodyItem = {
  offerId: string
  type: string
  price: number
  location: Array<string>
  vendor: string
  transactionKey: string
  mx?: string
  additionalInfo?: Record<string, string>
}

type CalculateCartCommissionBody = {
  cartId: string
  items: Array<CalculateCartCommissionBodyItem>
  total: number
  transactionKey: string
}

const NEW_TOUR_PRODUCT_TYPES = [PRODUCT_TYPE_TOUR_DELUXE, PRODUCT_TYPE_TOUR_PREMIUM, PRODUCT_TYPE_TOUR_ULTRA_LUX, PRODUCT_TYPE_TOUR_SIGNATURE_SERIES]

const getTourV2StartLocation = (item: App.Checkout.TourV2AccommodationOfferView) => {
  const variations = item.itemViews.map((x) => x.variation)
  if (variations.length === 0) return ['']
  return [variations[0]?.startLocation ?? '']
}

const sumViewItemTotal = (itemView: App.Checkout.AnyItemView): number => {
  const itemPrice = itemView.totals.price
  const isTourV2 = itemView.item.itemType === 'tourV2'
  const itemTotalPrice = isTourV2 ? itemView.item?.total ?? itemPrice : itemPrice
  const fullPrice = itemTotalPrice + (itemView.totals.surcharge ?? 0) + (itemView.totals.otherFees?.extraGuestSurcharge ?? 0)
  const fullPriceSubtractedPaidPrice = fullPrice - (itemView.totals.paidPrice ?? 0)
  return isTourV2 ? Math.max(fullPriceSubtractedPaidPrice, 0) : fullPrice
}
const sumViewItemsTotal = (items: Array<App.Checkout.AnyItemView>): number => {
  return sum(items, (i) => sumViewItemTotal(i))
}

const processExperienceItems = (experienceItemsView: Array<App.Checkout.ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []

  for (const item of experienceItemsView) {
    for (const ticket of item.ticketViews) {
      items.push({
        offerId: item.experienceId,
        type: OFFER_TYPE_EXPERIENCE,
        price: ticket.item.total ?? item.totals.price / (item.ticketViews.length ?? 1),
        location: [item.location ?? ''],
        vendor: '',
        transactionKey: ticket.item.transactionKey,
      })
    }
  }

  return items
}

const processTransferItems = (transferItemsView: Array<App.Checkout.TransferItemView> = []): Array<CalculateCartCommissionBodyItem> => {
  return transferItemsView.map((itemView) => ({
    offerId: itemView.item.experienceId,
    type: OFFER_TYPE_EXPERIENCE_TRANSFER,
    price: itemView.item.transfer.option?.price ?? 0,
    location: [],
    vendor: '',
    transactionKey: itemView.item.transactionKey,
  }))
}

const processCarHireItems = (carHireItemsView: Array<App.Checkout.CarHireItemView>): Array<CalculateCartCommissionBodyItem> => {
  return (carHireItemsView ?? []).map((itemView) => ({
    offerId: itemView.item.offerId,
    type: PRODUCT_TYPE_CAR_HIRE,
    price: itemView.item.price,
    location: [],
    vendor: '',
    transactionKey: itemView.item.transactionKey,
  }))
}

const processBedBankItems = (item: App.Checkout.BedbankAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  const locations = [
    item?.offer?.property?.address.city ?? '',
    item?.offer?.property?.address.stateProvinceName ?? '',
    item.offer?.property?.address.countryName ?? '',
  ].filter(Boolean)

  const transactionKeyGroupedBySessionId: Record<string, string> = {}

  return item.itemViews.map((itemView) => {
    let transactionKey = itemView.item.transactionKey
    const sessionId = itemView.item.sessionId
    if (sessionId) {
      const transactionKeyGroup = transactionKeyGroupedBySessionId[sessionId]
      if (transactionKeyGroup) {
        transactionKey = transactionKeyGroup
      } else {
        transactionKeyGroupedBySessionId[sessionId] = transactionKey
      }
    }

    return {
      type: item.offer?.productType ?? '',
      offerId: item.offerId,
      price: sumViewItemTotal(itemView),
      location: locations,
      vendor: '',
      transactionKey,
      mx: itemView.rate?.mx,
    }
  })
}

const processTourV2Items = (item: App.Checkout.TourV2AccommodationOfferView, allOptionalExperiences: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []

  const currentTourOptionalExperiences = allOptionalExperiences.filter((oe) => oe.item.offerId === item.offerId)
  const leahProductType = NEW_TOUR_PRODUCT_TYPES.includes(item.offer?.productType ?? '') ?
    DIRECT_TOUR :
    item.offer?.productType ?? ''

  items.push({
    type: leahProductType,
    offerId: item.offerId,
    price: sumViewItemsTotal(item.itemViews),
    location: getTourV2StartLocation(item),
    vendor: '',
    transactionKey: item.itemViews[0].item.transactionKey,
  })

  for (const optionalExperience of currentTourOptionalExperiences) {
    items.push(...processTourV2OptionalExperienceItems([optionalExperience]))
  }

  return items
}

const processCruiseItems = (item: App.Checkout.CruiseAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  return item.itemViews.map((itemView) => ({
    type: item.offer?.productType ?? '',
    offerId: item.offerId,
    price: sumViewItemTotal(itemView),
    location: [item.location],
    vendor: '',
    transactionKey: itemView.item.transactionKey,
  }))
}

const processAlwaysOnItems = (item: App.Checkout.LEAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  const locations = unique((item?.offer?.locations ?? []).concat(item?.offer?.location ?? []))

  return item.itemViews.map((itemView) => ({
    offerId: item.offerId,
    type: item.offer?.productType ?? item.offerType,
    price: sumViewItemTotal(itemView),
    location: locations,
    vendor: item.offer?.vendorName ?? '',
    transactionKey: itemView.item.transactionKey,
    ...(itemView.pkg?.roomRate?.mx && { mx: itemView.pkg?.roomRate?.mx }),
  }))
}

const processDefaultItems = (item: App.Checkout.LEAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  const locations = unique((item?.offer?.locations ?? []).concat(item?.offer?.location ?? []))

  return item.itemViews.map((itemView) => ({
    offerId: item.offerId,
    type: item.offer?.productType ?? item.offerType,
    price: sumViewItemTotal(itemView),
    location: locations,
    vendor: item.offer?.vendorName ?? '',
    transactionKey: itemView.item.transactionKey,
    ...(itemView.pkg?.mx && { mx: itemView.pkg?.mx }),
  }))
}

const processAccommodationItem = (item: App.Checkout.AccommodationOfferView, allOptionalExperiences: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  switch (item.offerType) {
    case OFFER_TYPE_TOUR:
      return processDefaultItems(item)
    case OFFER_TYPE_BED_BANK:
      return processBedBankItems(item)
    case OFFER_TYPE_TOUR_V2:
      return processTourV2Items(item, allOptionalExperiences)
    case OFFER_TYPE_CRUISE:
      return processCruiseItems(item)
    case OFFER_TYPE_ALWAYS_ON:
      return processAlwaysOnItems(item)
    default:
      return processDefaultItems(item)
  }
}

const processTourV2OptionalExperienceItems = (data: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  return data.map((item) => ({
    type: DIRECT_TOUR,
    // must be the id of the tour so that it applies the same commission as the tour
    offerId: item.item.purchasableOption.fkTourId,
    price: item.item.total ?? item.item.purchasableOption.fullPrice,
    location: [],
    vendor: '',
    transactionKey: item.item.transactionKey,
    additionalInfo: {
      type: item.item.itemType,
      optionalExperienceId: item.item.offerId,
    }
  }))
}

const getCommissionItems = (state: App.State): Array<CalculateCartCommissionBodyItem> => {
  const { accommodationItemsView, tourV2ExperienceItemsView, experienceItemsView, transferItemsView, carHireItemsView } =
    getAllItemViewsByType(state).data
  const allOptionalExperiences = tourV2ExperienceItemsView?.data ?? []

  const experienceItems = processExperienceItems(experienceItemsView.data)
  const transferItems = processTransferItems(transferItemsView.data)
  const carHireItems = processCarHireItems(carHireItemsView.data)

  const accommodationItems = accommodationItemsView.data.flatMap((item) => processAccommodationItem(item, allOptionalExperiences))

  // when checkout is type "tour-optional-extras", it allows the user to have an optional experience without the tour item in the checkout
  // so we must process them outside of the "tourV2" scope
  const isOptionalExperiencesCheckout = state.checkout.cart.mode === 'tour-optional-experiences'
  const tourOptionalExperiences = isOptionalExperiencesCheckout ? processTourV2OptionalExperienceItems(allOptionalExperiences) : []

  return [...experienceItems, ...transferItems, ...carHireItems, ...accommodationItems, ...tourOptionalExperiences]
}

export const stateToCommissionOrder = (state: App.State): CalculateCartCommissionBody => {
  const items = getCommissionItems(state)
  return {
    items,
    cartId: state.checkout.cart.cartId,
    total: sum(items, (i) => i.price ?? 0),
    transactionKey: state.checkout.cart.transactionKey,
  }
}

export const agentHubCommissionMap = (serverCommission: ServerAgentCommission): App.AgentHubCommission => {
  return {
    promoCode: serverCommission.promoCode,
    itemsCommission: Object.fromEntries(
      Object.entries(serverCommission.itemsCommission).map(([key, value]) => [
        key,
        {
          commissionPercentage: value.commissionPercentage,
          itemDiscount: value.itemDiscount,
        },
      ]),
    ),
  }
}

export const mapOfferCommission = (serverOfferCommission: ServerOfferCommission): App.AgentHubOfferCommission => {
  return {
    offerId: serverOfferCommission.offerId,
    commissionPercentage: serverOfferCommission.commission,
    packages: serverOfferCommission.packages.map((pkg) => ({
      packageId: pkg.packageId,
      commissionPercentage: pkg.commission,
    })),
    minBaseCommission: serverOfferCommission.minBaseCommission,
    maxBaseCommission: serverOfferCommission.maxBaseCommission,
  }
}

export const mapOfferToAgentHubCommissionPayload = (offer: App.AgentHubOfferPayload, offerRates: Record<string, Array<App.BedbankRate>> = {}): AgentHubGetOffersCommissionBody => {
  const mappedData: AgentHubGetOffersCommissionBody = match(offer)
    .when(isCarHireOffer, (carOffer) => ({
      offerId: carOffer.id,
      type: carOffer.productType,
      vendor: carOffer.vendor.name,
      price: carOffer.price,
      location: [carOffer.pickUp.name, carOffer.dropOff.name],
      packages: [],
    }))
    .when(isBedbankOffer, (bedbankOffer) => ({
      type: bedbankOffer.productType,
      offerId: bedbankOffer.id,
      vendor: '',
      price: bedbankOffer.packages[0].sell?.value ?? 0,
      location: [bedbankOffer.property.address.city],
      packages: Object.keys(offerRates).length > 0 ? offerRates[Object.keys(offerRates)[0]].map(mapBedbankPackageToAgentHubPayload) : [],
    }))
    .when(isTourV2Offer, (tourOffer) => {
      const variation = Object.keys(tourOffer.variations)[0]
      return {
        offerId: tourOffer.id,
        type: NEW_TOUR_PRODUCT_TYPES.includes(tourOffer.productType) ? DIRECT_TOUR : tourOffer.productType,
        vendor: '',
        price: 0,
        location: [tourOffer.variations[variation]?.startLocation ?? ''],
        packages: [],
      }
    })
    .when(isExperienceOffer, (expOffer) => ({
      offerId: expOffer.id,
      type: expOffer.productType !== OFFER_TYPE_EXPERIENCE_TRANSFER ? OFFER_TYPE_EXPERIENCE as App.OfferType : expOffer.productType,
      vendor: expOffer.vendor?.name ?? '',
      price: expOffer.price,
      location: [expOffer.location.name],
      packages: [],
    }))
    .when(isCruiseOffer, (cruiseOffer) => ({
      offerId: cruiseOffer.id,
      type: cruiseOffer.productType,
      vendor: cruiseOffer.cruiseLine?.vendorInfo?.[0]?.vendorName ?? '',
      price: cruiseOffer.mainDepartureDetails.lowestOverallPriceDetails.total,
      location: [cruiseOffer.cruiseLine?.name ?? ''],
      packages: [],
    }))
    .otherwise((defaultOffer) => ({
      offerId: defaultOffer.id,
      type: defaultOffer.productType,
      vendor: defaultOffer.vendorName ?? '',
      price: defaultOffer.lowestPricePackage?.price ?? 0,
      location: [defaultOffer.location ?? ''],
      packages: getHotelPackages(defaultOffer),
    }))

  return {
    offerId: mappedData.offerId,
    location: mappedData.location,
    price: mappedData.price,
    type: mappedData.type,
    vendor: mappedData.vendor,
    packages: mappedData.packages,
  }
}

const getHotelPackages = (offer: App.Offer | App.TourOfferSummary): AgentHubGetOffersCommissionBody['packages'] => {
  return match(offer)
    .when(isLpcHotelOffer, (lpcOffer) => {
      if (!lpcOffer?.packages) {
        return []
      }

      return lpcOffer.packages.filter(pkg => !!pkg.roomRate?.mx).map(mapLPCPackageToAgentHubPayload)
    })
    .when(isLEHotel, (hotelOffer) => {
      if (!hotelOffer?.packages) {
        return []
      }

      return hotelOffer.packages.filter(pkg => !!pkg.mx).map(mapFlashPackageToAgentHubPayload)
    })
    .otherwise(() => [])
}
